import { isForeclosureLicenseTypeName } from "@/lib/foreclosure-portal";

export type ForeclosureOrdinanceVersionInput = {
  id: string;
  is_deleted?: boolean;
  effective_start_date: string;
  label?: string | null;
  ordinance?: string | null;
};

export type ForeclosureOrdinanceConfigInput = {
  license_type_id?: string;
  license_type_name: string;
  ordinance_start_date: string;
  deleted_at?: string | null;
  ordinance?: string | null;
  ordinance_versions?: ForeclosureOrdinanceVersionInput[];
};

export type ForeclosureHistoricOrdinance = {
  id: string;
  effectiveStartDate: string;
  label: string | null;
  ordinance: string | null;
};

export type ForeclosureOrdinanceSummary = {
  licenseTypeId: string;
  jurisdictionName: string;
  licenseTypeName: string;
  ordinanceStartDate: string;
  ordinance: string | null;
  historic: ForeclosureHistoricOrdinance[];
};

export type RegistrationRequirementQuestion = {
  id: string;
  question: string;
  informationText: string | null;
};

export function registrationRequirementQuestions(
  questions: Array<{
    id: string;
    question: string;
    question_type?: string | null;
    answer_type?: string | null;
    information_text?: string | null;
    display_up_front?: boolean | null;
    display_order?: number | null;
  }>,
): RegistrationRequirementQuestion[] {
  return questions
    .filter(
      (question) =>
        (question.question_type ?? "registration") === "registration" &&
        question.answer_type === "information" &&
        question.display_up_front === true,
    )
    .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999))
    .map((question) => ({
      id: question.id,
      question: question.question.trim(),
      informationText: question.information_text?.trim() || null,
    }))
    .filter((question) => question.question.length > 0);
}

function historicForConfig(
  config: ForeclosureOrdinanceConfigInput,
): ForeclosureHistoricOrdinance[] {
  const currentStart = config.ordinance_start_date;
  return [...(config.ordinance_versions ?? [])]
    .filter(
      (version) =>
        !version.is_deleted && version.effective_start_date !== currentStart,
    )
    .sort(
      (a, b) =>
        a.effective_start_date.localeCompare(b.effective_start_date) ||
        a.id.localeCompare(b.id),
    )
    .map((version) => ({
      id: version.id,
      effectiveStartDate: version.effective_start_date,
      label: version.label?.trim() || null,
      ordinance: version.ordinance?.trim() || null,
    }));
}

/** Foreclosure license configs for a jurisdiction, with current and historic ordinance facts. */
export function buildForeclosureOrdinanceSummaries(
  configs: ForeclosureOrdinanceConfigInput[],
  jurisdictionName: string,
): ForeclosureOrdinanceSummary[] {
  return configs
    .filter(
      (config) =>
        !config.deleted_at &&
        isForeclosureLicenseTypeName(config.license_type_name),
    )
    .sort((a, b) =>
      a.license_type_name.localeCompare(b.license_type_name, undefined, {
        sensitivity: "base",
      }),
    )
    .map((config) => ({
      licenseTypeId: config.license_type_id ?? "",
      jurisdictionName,
      licenseTypeName: config.license_type_name,
      ordinanceStartDate: config.ordinance_start_date,
      ordinance: config.ordinance?.trim() || null,
      historic: historicForConfig(config),
    }));
}
